/** Lifecycle of a customer print session, from QR shown on the kiosk to prints delivered. */
export const SessionStatus = {
    /** QR is on screen, nobody has opened it yet. */
    WAITING: 'WAITING',
    /** A phone opened the link and claimed the session; files are being uploaded / configured. */
    ACTIVE: 'ACTIVE',
    /** Customer submitted the order; a Razorpay order exists and payment is pending. */
    AWAITING_PAYMENT: 'AWAITING_PAYMENT',
    /** Payment captured; print job dispatched to the kiosk. */
    PAID: 'PAID',
    PRINTING: 'PRINTING',
    COMPLETED: 'COMPLETED',
    /** Printing failed after payment; needs owner attention / refund. */
    FAILED: 'FAILED',
    EXPIRED: 'EXPIRED',
    CANCELLED: 'CANCELLED',
};
export const TERMINAL_SESSION_STATUSES = [
    SessionStatus.COMPLETED,
    SessionStatus.FAILED,
    SessionStatus.EXPIRED,
    SessionStatus.CANCELLED,
];
export const PrintJobStatus = {
    QUEUED: 'QUEUED',
    PRINTING: 'PRINTING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
};
export const PaymentStatus = {
    CREATED: 'CREATED',
    CAPTURED: 'CAPTURED',
    FAILED: 'FAILED',
    REFUNDED: 'REFUNDED',
};
/**
 * How customers reach a kiosk. KIOSK: a self-service kiosk whose screen shows a new QR code for each customer, one
 * at a time. COUNTER: a shop counter with no screen: a printed QR sticker (a permanent link) lets many customers order
 * at once; jobs print on the counter PC in the order they're paid, each with a token number, and staff hand them over.
 * The API and the portal's URLs still call both "kiosks".
 */
export const KioskMode = {
    KIOSK: 'KIOSK',
    COUNTER: 'COUNTER',
};
/**
 * Shop counters: what happens when an order is paid online, or placed to pay in cash at the counter. ONLINE_AUTO:
 * online payments go to the printer straight away, cash orders wait for staff to press Print (the default for new
 * counters). AUTO: every order goes to the printer straight away (as at kiosks). STAFF: every order waits in the
 * Counter queue until staff press Print. Each order keeps the mode it was placed under.
 */
export const CounterPrintMode = {
    ONLINE_AUTO: 'ONLINE_AUTO',
    AUTO: 'AUTO',
    STAFF: 'STAFF',
};
/** The owner's choice in a counter's Settings, in the words the portal uses (in the order it lists them). */
export const COUNTER_PRINT_MODE_LABELS = {
    ONLINE_AUTO: 'Online payments print automatically, cash waits for Print',
    AUTO: 'Always print automatically',
    STAFF: 'Staff press Print for every order',
};
/**
 * The modes a counter can be set to: "cash waits for Print" only while it takes cash. Turning cash on or off moves the
 * mode with it (AUTO ↔ ONLINE_AUTO; online payments behave the same in both), so it never has a mode it can't use.
 */
export const counterPrintModes = (acceptCash) => acceptCash ? ['ONLINE_AUTO', 'AUTO', 'STAFF'] : ['AUTO', 'STAFF'];
export function printModeForCash(mode, acceptCash) {
    if (!acceptCash && mode === 'ONLINE_AUTO')
        return 'AUTO';
    if (acceptCash && mode === 'AUTO')
        return 'ONLINE_AUTO';
    return mode;
}
/** Whether an order placed at a counter in this mode waits for staff to press Print (`cash`: pay at the counter). */
export const waitsForStaff = (mode, cash) => mode === 'STAFF' || (mode === 'ONLINE_AUTO' && cash);
/** What customers and staff call it: "kiosk" or "counter". */
export const placeWord = (mode) => (mode === KioskMode.COUNTER ? 'counter' : 'kiosk');
/** Longest customer name kept on a counter order. */
export const CUSTOMER_NAME_MAX = 60;
export const UserRole = {
    /** Full control of the organization: kiosks, pricing, team. */
    OWNER: 'OWNER',
    /** Can view everything in the organization and record paper refills. */
    OPERATOR: 'OPERATOR',
};
export const FileStatus = {
    PROCESSING: 'PROCESSING',
    READY: 'READY',
    FAILED: 'FAILED',
    /** A password-protected PDF: waits for the customer to enter its password. */
    PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
};
/** Everything that happens on a kiosk is recorded as one of these for the owner's activity log. */
export const KioskEventType = {
    AGENT_CONNECTED: 'AGENT_CONNECTED',
    AGENT_DISCONNECTED: 'AGENT_DISCONNECTED',
    DISPLAY_CONNECTED: 'DISPLAY_CONNECTED',
    DISPLAY_DISCONNECTED: 'DISPLAY_DISCONNECTED',
    SESSION_CREATED: 'SESSION_CREATED',
    SESSION_CLAIMED: 'SESSION_CLAIMED',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    SESSION_CANCELLED: 'SESSION_CANCELLED',
    PAYMENT_CAPTURED: 'PAYMENT_CAPTURED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    PRINT_STARTED: 'PRINT_STARTED',
    PRINT_COMPLETED: 'PRINT_COMPLETED',
    PRINT_FAILED: 'PRINT_FAILED',
    PAPER_REFILLED: 'PAPER_REFILLED',
    PAPER_LOW: 'PAPER_LOW',
    PRINTER_ERROR: 'PRINTER_ERROR',
    CONFIG_UPDATED: 'CONFIG_UPDATED',
    KEY_ROTATED: 'KEY_ROTATED',
    /** Counter: staff handed the prints over. */
    ORDER_COLLECTED: 'ORDER_COLLECTED',
    /** Counter: staff sent a paid order to the printer again. */
    ORDER_REPRINTED: 'ORDER_REPRINTED',
    /** A failed paid order was refunded by the business (recorded by hand; no gateway refund). */
    PAYMENT_REFUNDED: 'PAYMENT_REFUNDED',
    /** Counter set to "Staff press Print": staff sent a paid order to the printer. */
    ORDER_SENT_TO_PRINTER: 'ORDER_SENT_TO_PRINTER',
};
export const ColorMode = { BW: 'BW', COLOR: 'COLOR' };
export const Orientation = { PORTRAIT: 'PORTRAIT', LANDSCAPE: 'LANDSCAPE' };
export const MAX_COPIES = 50;
export const DEFAULT_PRINT_OPTIONS = {
    colorMode: ColorMode.BW,
    copies: 1,
    orientation: Orientation.PORTRAIT,
    duplex: false,
};
//# sourceMappingURL=domain.js.map